import { PureComponent } from 'react';

import {
  Divider,
  Header,
  PrimaryButton,
  SecondaryButton,
  Spacer,
  SubText,
  TertiaryButton,
  Toolbar,
} from '@procurify/ui'
import { FormattedMessage } from 'react-intl'

import { ATTACHMENTS_URL } from '@webapp/api/AttachmentsAPI'
import { FormattedEnhancedMessage } from '@webapp/core/hoc/FormattedEnhancedMessage'
import { ImporterUploadStageStyled } from './styles/ImporterUploadStageStyled'
import {
  ACCEPTED_CSV_FILE_TYPES,
  ACCEPTED_ZIP_MIME_TYPES,
  CSV_LABEL,
  ZIP_LABEL,
} from '../../constants'
import messages from '../../messages'
import { ImporterContentStyled as Content } from '../../styles/ImporterContentStyled'
import { ImporterCriteriaContainerStyled as CriteraContainer } from '../../styles/ImporterCriteriaContainerStyled'
import { ImporterUploaderContainerStyled as ImporterUploaderContainer } from '../../styles/ImporterUploaderContainerStyled'
import { type ImporterSampleData } from '../../types'
import { ImporterUploader } from '../ImporterUploader/ImporterUploader'

export const UNKNOWN_NUM_CSV_COLUMNS = -1

export interface IImporterUploadStageProps {
  title?: string
  criteria?: JSX.Element | JSX.Element[]
  importEndpoint?: string
  templateEndpoint?: string
  requiredFields?: string[]
  fieldNameMapper?: { [settingLabel: string]: string }
  onFileAdded?: () => void
  onCSVUpload?: (file) => void
  onCSVRemove?: () => void
  onSubmit?: () => void
  onAssetUpload?: (file) => void
  onAssetRemove?: () => void
  onUploadError?: (errorCode?: string) => void
  onDismiss?: () => void
  showUploadAssets?: boolean
  disableSubmitButton?: boolean
  numColumnsInCSV?: number
  isButtonLoading?: boolean
}

export interface IImporterUploadStageState {
  uploadedCSV: string
  uploadedAssets: string
  sampleData: ImporterSampleData
}

const initialState = {
  uploadedCSV: '',
  uploadedAssets: '',
  sampleData: null,
}

export class ImporterUploadStage extends PureComponent<
  IImporterUploadStageProps,
  IImporterUploadStageState
> {
  static readonly defaultProps = {
    requiredFields: [],
    importEndpoint: ATTACHMENTS_URL,
    showUploadAssets: true,
    isButtonLoading: false,
  }
  readonly state = initialState

  generateCriteria = () => {
    const { requiredFields, fieldNameMapper } = this.props

    return (
      <ul>
        {requiredFields.map((field, index) => {
          const label =
            fieldNameMapper && fieldNameMapper[field]
              ? fieldNameMapper[field]
              : field
          return <li key={index}>{label}</li>
        })}
      </ul>
    )
  }

  render() {
    const {
      title,
      criteria,
      templateEndpoint,
      showUploadAssets,
      requiredFields,
      numColumnsInCSV,
    } = this.props

    return (
      <Content>
        <ImporterUploadStageStyled>
          <Header as='h3' gutter>
            {title}
          </Header>

          <ImporterUploaderContainer>
            <ImporterUploader
              id={title}
              icon='csv'
              endpoint={this.props.importEndpoint}
              onUpload={this.props.onCSVUpload}
              onFileAdded={this.props.onFileAdded}
              onFileRemove={this.props.onCSVRemove}
              validFileTypes={ACCEPTED_CSV_FILE_TYPES}
              validFileTypeName={CSV_LABEL}
            />
            {showUploadAssets && (
              <ImporterUploader
                id={`${title} assets`}
                icon='zip'
                onUpload={this.props.onAssetUpload}
                onFileRemove={this.props.onAssetRemove}
                validFileTypes={ACCEPTED_ZIP_MIME_TYPES}
                validFileTypeName={ZIP_LABEL}
              />
            )}
          </ImporterUploaderContainer>
          {numColumnsInCSV < requiredFields.length &&
            (numColumnsInCSV === UNKNOWN_NUM_CSV_COLUMNS ? (
              <SubText color='danger'>
                <FormattedMessage
                  {...messages.incompleteErrorStepDescription}
                  values={{ minColumns: requiredFields.length }}
                />
              </SubText>
            ) : (
              <SubText color='danger'>
                <FormattedMessage
                  {...messages.incompleteFileErrorMessage}
                  values={{
                    numCols: numColumnsInCSV,
                    numReqFields: requiredFields.length,
                  }}
                />
              </SubText>
            ))}

          <Spacer space={2} />

          <SubText gutter>
            <FormattedMessage {...messages.uploadingStepCriteriaTitle} />
          </SubText>

          <CriteraContainer>
            {criteria || this.generateCriteria()}
          </CriteraContainer>

          <Spacer space={2} />

          <Toolbar>
            <Toolbar.Fill />
            <Toolbar.Item>
              <TertiaryButton
                className='dismiss-btn'
                onClick={this.props.onDismiss}
              >
                <FormattedMessage {...messages.uploadingStepCancelButton} />
              </TertiaryButton>
              <PrimaryButton
                className='submit-csv-btn'
                disabled={this.props.disableSubmitButton}
                onClick={this.props.onSubmit}
                isLoading={this.props.isButtonLoading}
              >
                <FormattedMessage {...messages.uploadingStepImportButton} />
              </PrimaryButton>
            </Toolbar.Item>
          </Toolbar>

          {templateEndpoint && (
            <>
              <Divider space={2} />
              <Header as='h3' gutter>
                <FormattedMessage {...messages.uploadingStepTemplateTitle} />
              </Header>
              <SubText gutter>
                <FormattedEnhancedMessage
                  values={{ title: this.props.title }}
                  {...messages.uploadingStepTemplateDescription}
                />
              </SubText>
              <a href={templateEndpoint}>
                <SecondaryButton>
                  <FormattedMessage
                    {...messages.uploadingStepDownloadTemplateButton}
                  />
                </SecondaryButton>
              </a>
            </>
          )}
        </ImporterUploadStageStyled>
      </Content>
    )
  }
}
